import { PaywallResult } from '@accedo/one-plugin-sdk';
import {
  ActionKind,
  ActionsDictionary,
  ActionTypes,
  AuthView,
  eventHandlers,
} from '@dynamic/ui';
import { RefObject } from 'react';

import { Item, ResourceType, userService } from '#/client';
import { mediaTypeToResourceType } from '#/components/GlobalState';
import { SharingModalApi } from '#/components/Modal';
import { privacyStoreActions } from '#/plugins/privacy/privacyStore';
import { NavigationType } from '#/service/constants';
import { InternalPageId } from '#/service/constants/menu';
import { destinationRoutes, Path } from '#/service/routes';
import { ignoreError } from '#/service/utils';
import { authActions } from '#/store/auth';
import { favoritesActions } from '#/store/favorites';
import { upNextStoreActions } from '#/store/upNext/upNext.store';

import { useNavigation } from '../useNavigation';
import { usePaywall } from '../usePaywall';
import { generatePathname } from './pathUtils';

export const useActions = (sharingModalRef?: RefObject<SharingModalApi>) => {
  const { go } = useNavigation('useActions');
  const { showPaywall } = usePaywall();

  const paywallAction = () => {
    showPaywall()
      .then(async (res) => {
        console.debug('Action Hook - Paywall result', res);

        if ([PaywallResult.Payed, PaywallResult.HasAccess].includes(res)) {
          // refresh profile, and update auth state after payment
          await authActions.refreshProfile();
        }
      })
      .catch((err) => {
        console.error('Action Hook - Paywall error', err);
      });
  };

  const playAction =
    (action: string): ActionsDictionary[ActionKind.Play] =>
    async (resourceId, mediaType, item?: Item) => {
      upNextStoreActions.setStartFromBeginning(false);
      const resourceType = mediaTypeToResourceType[mediaType] || mediaType;
      console.debug(
        `Action Hook - Action: ${action}`,
        resourceId,
        resourceType
      );
      // DynUi can pass back any resourceType/resourceId they want, we care about the current item in context specifically here
      const itemSpecificResourceType =
        item?.type?.toLocaleLowerCase?.() as ResourceType;
      const itemRefs =
        itemSpecificResourceType === ResourceType.Collection
          ? item?.upNext?.refs
          : item?.refs;
      go(`/playback/${resourceType}/${resourceId}`, itemRefs);
    };

  const shareAction =
    (action: string): ActionsDictionary[ActionKind.Share] =>
    (paramUrl, item: Item) => {
      console.debug(`Action Hook - Action: ${action}`, paramUrl, item);
      sharingModalRef?.current?.open(item);
    };

  const purchaseAction =
    (action: string): ActionsDictionary[ActionKind.Purchase] =>
    async (resourceId, mediaType, contextItem?: Item) => {
      const resourceType =
        (mediaType && mediaTypeToResourceType[mediaType]) || mediaType;
      console.debug(`Action Hook - Action: ${action}`, {
        resourceId,
        resourceType,
        contextItem,
      });
      authActions.setPurchaseEntitlements(contextItem?.video?.entitlementTags);
      paywallAction();
    };

  const openPrivacyAction =
    (action: string): ActionsDictionary[ActionKind.OpenPrivacyLawSettings] =>
    () => {
      console.debug(`Action Hook - Action: ${action}`);
      privacyStoreActions.showCpUi();
    };

  const actions: ActionsDictionary = {
    [ActionKind.NavigateExternal]: (link) => {
      console.debug('Action Hook - Action: NavigateExternal', link);

      window.open(link, '_blank');
    },

    [ActionKind.NavigateInternal]: (view) => {
      console.debug('Action Hook - Action: NavigateInternal', view);

      // TODO: Handle all possible `view` values once the spec is finalised. As of today, it says "TBD".
      //  https://github.com/Accedo-Products/one-dynamic-ui-specification/blob/master/docs/components.md#navigate_to_internal_view-action-parameters-object

      switch (view) {
        case InternalPageId.Iap:
          paywallAction();
          break;
        case InternalPageId.Login:
          authActions.showLogin();
          break;
        case InternalPageId.Register:
        case InternalPageId.WatchHistory:
        case InternalPageId.Search:
        case InternalPageId.Favorites:
        case InternalPageId.ProgramList:
        case InternalPageId.Settings:
        case InternalPageId.ForgotPassword:
          go(destinationRoutes[view]);
          break;
        default:
          // treat unknown `view` values as relative paths
          go('/' + view);
      }
    },

    [ActionKind.NavigatePage]: (params, item?: Item) => {
      console.debug('Action Hook - Action: NavigatePage', params);
      const { pageAlias, pageId, context = {} } = params;

      const pathname = generatePathname({
        pageId: pageId || pageAlias || '',
        resourceId: context.objectId,
        resourceType: context.objectType as ResourceType,
        navigationType: NavigationType.Page,
        itemName: item?.title,
      });

      const itemRefs = item?.refs;
      go(pathname, itemRefs);
    },
    // supporting deprecated action type for backward compatibility
    [ActionTypes.Navigate]: /*NOSONAR*/ (
      pageId,
      resourceId,
      mediaType,
      navigationType,
      item?: Item
    ) => {
      const resourceType = mediaTypeToResourceType[mediaType] || mediaType;
      console.debug(
        'Action Hook - Action: navigate',
        pageId,
        resourceId,
        resourceType,
        navigationType,
        item
      );

      if (navigationType === NavigationType.External) {
        window.open(pageId, '_blank');

        return;
      }

      const pathname = generatePathname({
        pageId,
        resourceId,
        resourceType,
        navigationType,
        itemName: item?.title,
      });
      const itemRefs = item?.refs;

      if (pathname) {
        go(pathname, itemRefs);
      }
    },

    [ActionTypes.Play]: playAction(ActionTypes.Play),

    [ActionKind.Play]: playAction(ActionKind.Play),

    [ActionTypes.Rate]: (videoId) => {
      console.debug('Action Hook - Action: rate', videoId);
      // TODO: not implemented yet
    },

    [ActionTypes.Share]: shareAction(ActionTypes.Share),

    [ActionKind.Share]: shareAction(ActionKind.Share),

    [ActionTypes.Authenticate]: (initialView = AuthView.Login) => {
      console.debug('Action Hook - Action: authenticate', initialView);

      if (initialView === AuthView.Login) {
        authActions.showLogin();
      } else if (initialView === AuthView.Registration) {
        go(Path.Registration);
      } else {
        console.warn('Action Hook - Invalid auth initial view');
      }
    },

    [ActionTypes.Purchase]: purchaseAction(ActionTypes.Purchase),

    [ActionKind.Purchase]: purchaseAction(ActionKind.Purchase),

    [ActionKind.RemoveFromFavorites]: (itemId, mediaType) => {
      console.debug(`Action Hook - Action: ${ActionKind.RemoveFromFavorites}`);
      favoritesActions.removeFavorite(itemId, mediaType).catch(ignoreError);
    },

    [ActionKind.AddToFavorites]: (itemId, mediaType) => {
      console.debug(`Action Hook - Action: ${ActionKind.AddToFavorites}`);
      favoritesActions.addFavorite(itemId, mediaType).catch(ignoreError);
    },

    [ActionKind.OpenPrivacyLawSettings]: openPrivacyAction(
      ActionKind.OpenPrivacyLawSettings
    ),

    [ActionTypes.OpenPrivacyLawSettings]: openPrivacyAction(
      ActionTypes.OpenPrivacyLawSettings
    ),

    [ActionKind.Register]: (resourceId, mediaType) => {
      const resourceType =
        (mediaType && mediaTypeToResourceType[mediaType]) || mediaType;
      console.debug(`Action Hook - Action: ${ActionKind.Register}`, {
        resourceId,
        resourceType,
      });
      go(Path.Registration);
    },

    [ActionKind.Logout]: () => {
      console.debug(`Action Hook - Action: ${ActionKind.Logout}`);
      userService.logout().then(authActions.logout).catch(ignoreError);
    },

    [ActionKind.Login]: (resourceId, mediaType, contextItem?: Item) => {
      const resourceType =
        (mediaType && mediaTypeToResourceType[mediaType]) || mediaType;
      console.debug(`Action Hook - Action: ${ActionKind.Login}`, {
        resourceId,
        resourceType,
        contextItem,
      });
      authActions.setPurchaseEntitlements(contextItem?.video?.entitlementTags);
      authActions.showLogin();
    },
  };

  const handleAction = (action?: object, context = {}) => {
    if (!action) {
      return;
    }
    eventHandlers(context, actions, action)?.onClick();
  };

  return {
    actions,
    handleAction,
  };
};
